import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export interface TermConfiguration {
  term: number;
  isActive: boolean;
  minLoanRequired: number;
  disabledStates: string[];
}

export interface LenderTerm {
  id: string;
  term: number;
  rate: { min: number; max: number };
  isDefault: boolean;
  assignedStates: string[];
}

export interface Lender {
  id: string;
  name: string;
  downpayment: number; // Can be percentage (0-1) or fixed amount (e.g., 99, 199)
  canSplit: boolean;
  display: boolean;
  terms: LenderTerm[];
  disabledStates?: string[];
  termConfigurations: TermConfiguration[];
}

const DEFAULT_TERMS = [24, 36, 60];

function createSelectLender(terms: number[] = DEFAULT_TERMS): Lender {
  return {
    id: 'select-lender',
    name: 'Select Lender',
    downpayment: 0,
    canSplit: false,
    display: true,
    terms: terms.map((term) => ({
      id: `select-lender-${term}`,
      term,
      rate: { min: 0, max: 0 },
      isDefault: true,
      assignedStates: [],
    })),
    disabledStates: [],
    termConfigurations: terms.map((term) => ({
      term,
      isActive: true,
      minLoanRequired: 0,
      disabledStates: [],
    })),
  };
}

export const SELECT_LENDER: Lender = createSelectLender();

/**
 * Fetches lenders from Firebase 'lenders-prod' collection
 * Only returns lenders where display === true
 * @returns {Promise<Lender[]>} Promise with array of Lender objects
 */
export async function fetchLenders(): Promise<Lender[]> {
  try {
    const LENDERS_COLLECTION =
      process.env.REACT_APP_LENDERS_COLLECTION || 'lenders-prod';
    const collectionRef = collection(db, LENDERS_COLLECTION);
    const snapshot = await getDocs(collectionRef);
    const lenders = snapshot.docs
      .map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          } as Lender)
      )
      .filter((lender) => lender.display === true)
      .sort((a, b) => a.name.localeCompare(b.name));

    return [SELECT_LENDER, ...lenders];
  } catch (error) {
    console.error('Error fetching lenders:', error);
    throw error;
  }
}
